import { CameraCapture } from "@khazzin/ui/patterns/CameraCapture";
import { Button, ConfirmDialog, Drawer, Field, Input, Textarea } from "@khazzin/ui/primitives";
import { useState } from "react";
import { useI18n } from "../../i18n";
import {
	canSubmitFailed,
	FAILURE_REASONS,
	type FailedDraft,
	failedStarted,
	requestableDays,
} from "./outcome";

/**
 * The Failed sheet (DA-10-19, EP10-S17): why the parcel was not delivered,
 * the customer's day when they postponed, a note, and a photo — required
 * whatever the reason (owner, ADJ-1022-8b). Submit stays disabled until the reason and what
 * it needs are there; the server refuses the same gaps (`422`) for a call that
 * bypasses the phone. Every change is checkpointed by the caller, so a phone
 * killed mid-sheet reopens here with the reason and the photo.
 *
 * On a phone the Drawer is a full page, which is the bottom sheet's job here.
 */
export interface FailedSheetProps {
	draft: FailedDraft;
	photo: Blob | null;
	onChange: (next: FailedDraft) => void;
	onPhoto: (photo: Blob) => void;
	onCancel: () => void;
	/** Queues the attempt; answers a message key when it cannot. */
	onSubmit: () => Promise<string | null>;
}

export function FailedSheet({
	draft,
	photo,
	onChange,
	onPhoto,
	onCancel,
	onSubmit,
}: FailedSheetProps) {
	const { t } = useI18n();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Cancel asks as the back arrow does (ADJ-M03-4).
	const [asking, setAsking] = useState(false);
	const days = requestableDays(new Date());

	async function submit() {
		setBusy(true);
		setError(null);
		try {
			setError(await onSubmit());
		} finally {
			setBusy(false);
		}
	}

	return (
		<Drawer
			open
			onOpenChange={(open) => {
				if (!open) {
					onCancel();
				}
			}}
			title={t("driver.outcome.failedTitle")}
			// ADJ-M03-4: a stray back or Escape must not throw away the reason
			// and the photo the driver took.
			dirty={failedStarted(draft)}
			discard={{
				title: t("driver.outcome.discardTitle"),
				description: t("driver.outcome.discardFailed"),
				confirmLabel: t("driver.outcome.discard"),
				cancelLabel: t("driver.outcome.keepEditing"),
			}}
			closeLabel={t("driver.outcome.cancel")}
			backLabel={t("driver.outcome.cancel")}
			footer={
				<>
					<Button onClick={() => (failedStarted(draft) ? setAsking(true) : onCancel())}>
						{t("driver.outcome.cancel")}
					</Button>
					<Button
						variant="danger"
						size="lg"
						disabled={!canSubmitFailed(draft)}
						loading={busy}
						onClick={() => void submit()}
					>
						{t("driver.outcome.submit")}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-4">
				<fieldset className="flex flex-col gap-1">
					<legend className="mb-2 text-[14px] font-bold text-fg">
						{t("driver.outcome.reason")}
					</legend>
					{FAILURE_REASONS.map((reason) => (
						<label
							key={reason}
							className="flex min-h-12 items-center gap-3 rounded-lg border border-border px-3 text-[15px] text-fg has-checked:border-brand has-checked:bg-brand-subtle"
						>
							<input
								type="radio"
								name="failure-reason"
								className="size-5 accent-brand"
								checked={draft.reason === reason}
								onChange={() => onChange({ ...draft, reason })}
							/>
							{t(`delivery.failureReason.${reason}`)}
						</label>
					))}
				</fieldset>

				{draft.reason === "customer_postponed" && (
					<Field label={t("driver.outcome.requestedDate")} required>
						{(props) => (
							<Input
								{...props}
								type="date"
								min={days.min}
								max={days.max}
								value={draft.requestedDate}
								onChange={(e) => onChange({ ...draft, requestedDate: e.target.value })}
							/>
						)}
					</Field>
				)}

				<Field
					label={t("driver.outcome.note")}
					required={draft.reason === "other"}
					optional={draft.reason !== "other"}
				>
					{(props) => (
						<Textarea
							{...props}
							maxLength={500}
							value={draft.note}
							onChange={(e) => onChange({ ...draft, note: e.target.value })}
						/>
					)}
				</Field>

				<div className="flex flex-col gap-1">
					<p className="text-[14px] font-bold text-fg">{t("driver.outcome.photo")}</p>
					<p className="text-[13px] text-fg-muted">{t("driver.outcome.photoRequired")}</p>
					<CameraCapture
						label={t("driver.outcome.takePhoto")}
						retakeLabel={t("driver.outcome.retakePhoto")}
						previewLabel={t("driver.outcome.photoPreview")}
						photo={photo}
						onCapture={onPhoto}
					/>
				</div>

				{error !== null && (
					<p role="alert" className="text-[13px] text-danger-fg">
						{t(error)}
					</p>
				)}
			</div>
			<ConfirmDialog
				open={asking}
				onOpenChange={setAsking}
				tone="danger"
				title={t("driver.outcome.discardTitle")}
				description={t("driver.outcome.discardFailed")}
				confirmLabel={t("driver.outcome.discard")}
				cancelLabel={t("driver.outcome.keepEditing")}
				onConfirm={() => {
					setAsking(false);
					onCancel();
				}}
			/>
		</Drawer>
	);
}
